import { BaseComponent } from "./base_component"
import { NodeData, WorkerInputs, WorkerOutputs } from 'rete/types/core/data'
import { Node, Output } from 'rete'
import { BooleanTileGrid } from "../tile_grid"
import { booleanDataSocket } from "../socket_types"
import { CheckboxSelectControl } from "../controls/checkbox-select"
import { PreviewControl } from "../controls/preview"
import { OSMCustomQueryControl } from "../controls/osm-custom-query"
import { normalizeOsmCustomQueries } from "../controls/osm_custom_query"
import { Extent } from "ol/extent"
import { createXYZ } from "ol/tilegrid"
import * as proj4 from "proj4"
import { Point, Polygon, LineString } from "ol/geom"
import { maskFromExtentAndShape } from "../bounding_box"
import { ProjectProperties } from "."

interface LandUseCategory {
    code: string
    name: string
    id: number
    type: "node" | "way" | "relation"
}

interface OverpassCoord {
    lat: number
    lon: number
}

interface OverpassFeature {
    type: string
    id: number
    bounds: object
    tags: object
    geometry: Array<OverpassCoord>
    lat: number
    lon: number
}

interface OverpassResponse {
    elements: OverpassFeature[]
    version?: number
    generator?: string
}

/**
 * Overpass API endpoints (fallback servers)
 */
const OVERPASS_ENDPOINTS = [
    'https://overpass-api.de/api/interpreter',
    'https://overpass.kumi.systems/api/interpreter',
    'https://lz4.overpass-api.de/api/interpreter',
]

/**
 * Retrieves land use data from Overpass API with retry logic and fallback servers
 * @param extent - Bounding box in EPSG:3857
 * @param landuse - OSM tag filter (e.g., "[landuse=residential]")
 * @param type - OSM element type (node, way, or relation)
 * @param retries - Number of retries (default: 3)
 * @returns Promise with Overpass API response
 */
async function retrieveLandUseData(
    extent: Extent,
    landuse: string,
    type: "node" | "way" | "relation",
    retries: number = 3
): Promise<OverpassResponse> {
    const [e0, e1] = [
        (proj4 as any).default('EPSG:3857', 'EPSG:4326', extent.slice(0, 2)),
        (proj4 as any).default('EPSG:3857', 'EPSG:4326', extent.slice(2, 4))
    ]

    // Calculate approximate area to determine timeout
    const latDiff = Math.abs(e1[0] - e0[0])
    const lonDiff = Math.abs(e1[1] - e0[1])
    const area = latDiff * lonDiff
    
    // Increase timeout for larger areas (max 180 seconds)
    // Base timeout of 60 seconds, add up to 120 seconds for large areas
    const timeout = Math.min(60 + Math.floor(area * 100), 180)

    const query = `
        [out:json][timeout:${timeout}][maxsize:536870912];
        ${type}(${e0[1]}, ${e0[0]}, ${e1[1]}, ${e1[0]})${landuse};
        out geom;
    `

    let lastError: Error | null = null

    // Try each endpoint
    for (const endpoint of OVERPASS_ENDPOINTS) {
        // Retry logic for each endpoint
        for (let attempt = 0; attempt < retries; attempt++) {
            try {
                if (attempt > 0) {
                    // Exponential backoff: wait 2^attempt seconds
                    const delay = Math.min(1000 * Math.pow(2, attempt), 10000)
                    await new Promise(resolve => setTimeout(resolve, delay))
                }

                const url = `${endpoint}?data=${encodeURIComponent(query)}`
                
                // Add abort controller for timeout
                const controller = new AbortController()
                const timeoutId = setTimeout(() => controller.abort(), (timeout + 10) * 1000)

                const response = await fetch(url, {
                    signal: controller.signal
                })
                
                clearTimeout(timeoutId)

                if (!response.ok) {
                    // If it's a server error (5xx), try next endpoint
                    if (response.status >= 500 && attempt < retries - 1) {
                        continue
                    }
                    throw new Error(`Overpass API error: ${response.status} ${response.statusText}`)
                }

                const json = await response.json()
                
                if (json.error) {
                    // Check for timeout or rate limit errors
                    const errorMsg = String(json.error).toLowerCase()
                    if ((errorMsg.includes('timeout') || errorMsg.includes('rate limit')) && attempt < retries - 1) {
                        // Wait longer before retry for timeout/rate limit
                        await new Promise(resolve => setTimeout(resolve, 5000 * (attempt + 1)))
                        continue
                    }
                    throw new Error(`Overpass API error: ${json.error}`)
                }

                // Success - return the result
                return json as OverpassResponse

            } catch (error: any) {
                lastError = error
                
                // If it's an abort (timeout), try next endpoint or retry
                if (error.name === 'AbortError') {
                    if (attempt < retries - 1) {
                        continue
                    }
                    // Move to next endpoint
                    break
                }
                
                // For network errors, try next endpoint
                if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
                    break // Try next endpoint
                }
                
                // For other errors, retry if attempts remain
                if (attempt < retries - 1) {
                    continue
                }
            }
        }
    }

    // All endpoints and retries failed
    throw lastError || new Error('Failed to retrieve data from all Overpass API endpoints')
}

export const LandUseCategories: LandUseCategory[] = [
    // Reference: https://wiki.openstreetmap.org/wiki/Land_use
    // https://wiki.openstreetmap.org/wiki/Key:landuse
    // Categories sorted alphabetically by name

    { code: "[aeroway=aerodrome]", name: "Aerodrome", id: 0, type: "way" },
    { code: "[landuse=allotments]", name: "Allotments", id: 1, type: "way" },
    { code: "[building=yes]", name: "Building", id: 2, type: "way" },
    { code: "[landuse=brownfield]", name: "Brownfield", id: 3, type: "way" },
    { code: "[landuse=cemetery]", name: "Cemetery", id: 4, type: "way" },
    { code: "[amenity=college]", name: "College", id: 5, type: "way" },
    { code: "[landuse=commercial]", name: "Commercial", id: 6, type: "way" },
    { code: "[landuse=construction]", name: "Construction", id: 7, type: "way" },
    { code: "[landuse=farmland]", name: "Farm Land", id: 8, type: "way" },
    { code: "[landuse=farmyard]", name: "Farm Yard", id: 9, type: "way" },
    { code: "[landuse=forest]", name: "Forest", id: 10, type: "way" },
    { code: "[leisure=garden]", name: "Garden", id: 11, type: "way" },
    { code: "[leisure=garages]", name: "Garages", id: 12, type: "way" },
    { code: "[landuse=greenfield]", name: "Greenfield", id: 13, type: "way" },
    { code: "[landuse=greenhouse_horticulture]", name: "Greenhouses", id: 14, type: "way" },
    { code: "[amenity=hospital]", name: "Hospital", id: 15, type: "way" },
    { code: "[landuse=industrial]", name: "Industrial", id: 16, type: "way" },
    { code: "[landuse=landfill]", name: "Landfill", id: 17, type: "way" },
    { code: "[landuse=meadow]", name: "Meadow / Pasture", id: 18, type: "way" },
    { code: "[landuse=military]", name: "Military", id: 19, type: "way" },
    { code: "[landuse=quarry]", name: "Mine (open pit) / Quarry", id: 20, type: "way" },
    { code: "[boundary=national_park]", name: "National Park", id: 21, type: "way" },
    { code: "[boundary=nature_reserve]", name: "Nature Reserve", id: 22, type: "way" },
    { code: "[landuse=orchard]", name: "Orchard / Plantation", id: 23, type: "way" },
    { code: "[leisure=park]", name: "Park", id: 24, type: "way" },
    { code: "[amenity=parking]", name: "Parking", id: 25, type: "way" },
    { code: "[landuse=plant_nursery]", name: "Plant Nursery", id: 26, type: "way" },
    { code: "[landuse=railway]", name: "Railway", id: 27, type: "way" },
    { code: "[landuse=recreation_ground]", name: "Recreation Ground", id: 28, type: "way" },
    { code: "[landuse=religious]", name: "Religious", id: 29, type: "way" },
    { code: "[landuse=residential]", name: "Residential", id: 30, type: "way" },
    { code: "[landuse=retail]", name: "Retail", id: 31, type: "way" },
    { code: "[landuse=salt_pond]", name: "Salt Pond", id: 32, type: "way" },
    { code: "[amenity=school]", name: "School", id: 33, type: "way" },
    { code: "[natural=tree]", name: "Tree", id: 34, type: "node" },
    { code: "[amenity=university]", name: "University", id: 35, type: "way" },
    { code: "[landuse=village_green]", name: "Village Green", id: 36, type: "way" },
    { code: "[landuse=vineyard]", name: "Vineyard", id: 37, type: "way" },
    { code: "[man_made=wastewater_plant]", name: "Waste water treatment", id: 38, type: "way" },
]

export class OSMLandUseComponentV2 extends BaseComponent {
    outputCache: Map<string, BooleanTileGrid>
    projectProps: ProjectProperties

    constructor(projectProps: ProjectProperties) {
        super("OpenStreetMap Land Use")
        this.category = "Inputs"
        this.projectProps = projectProps
        this.outputCache = new Map()
    }

    async builder(node: Node) {
        node.meta.toolTip = "This component uses OpenStreetMap API to generate boolean datasets for specific land use types. Multiple categories are combined with OR logic. The component includes retry logic and fallback servers to handle timeouts. Further information about the land use types can be found on their wiki. [click here for more info]"
        node.meta.toolTipLink = "https://wiki.openstreetmap.org/wiki/Key:landuse"

        node.addOutput(new Output('out', 'Output', booleanDataSocket))

        node.addControl(
            new CheckboxSelectControl(
                this.editor,
                'osmLandCategoryIds',
                () => LandUseCategories.map(cat => ({ id: cat.id, name: cat.name })),
                () => {},
                'Preset Categories'
            )
        )

        node.addControl(
            new OSMCustomQueryControl(
                this.editor,
                'osmCustomQueries',
                node.id.toString()
            )
        )

        node.addControl(new PreviewControl(() =>
            node.meta.output as any || new BooleanTileGrid(0, 0, 0, 1, 1)
        ))
    }

    async worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs, ...args: unknown[]) {
        const editorNode = this.editor?.nodes.find(n => n.id === node.id)
        if (editorNode === undefined) { return }

        try {
            // Get selected category IDs (array of strings)
            const selectedIds = (node.data.osmLandCategoryIds as string[] | undefined) || []
            
            // Get custom queries
            const customQueries = normalizeOsmCustomQueries(node.data.osmCustomQueries)
            const validCustomQueries = customQueries.filter(q => q.key.trim() !== "")

            // If no categories selected and no custom queries, return empty grid
            if (selectedIds.length === 0 && validCustomQueries.length === 0) {
                const tileGrid = createXYZ()
                const outputTileRange = tileGrid.getTileRangeForExtentAndZ(
                    this.projectProps.extent,
                    this.projectProps.zoom
                )
                const emptyResult = new BooleanTileGrid(
                    this.projectProps.zoom,
                    outputTileRange.minX,
                    outputTileRange.minY,
                    outputTileRange.getWidth(),
                    outputTileRange.getHeight()
                )
                emptyResult.name = "No categories or queries selected"
                editorNode.meta.output = outputs['out'] = emptyResult
                const previewControl: any = editorNode.controls.get('Preview')
                previewControl?.update()
                editorNode.update()
                return
            }

            // Convert string IDs to numbers and validate
            const selectedCategories = selectedIds
                .map(id => {
                    const index = parseInt(id, 10)
                    if (isNaN(index) || index < 0 || index >= LandUseCategories.length) {
                        return null
                    }
                    return LandUseCategories[index]
                })
                .filter((cat): cat is LandUseCategory => cat !== null)

            const mask = await maskFromExtentAndShape(this.projectProps)
            const tileGrid = createXYZ()
            const outputTileRange = tileGrid.getTileRangeForExtentAndZ(
                this.projectProps.extent,
                this.projectProps.zoom
            )

            // Initialize result grid
            const result = new BooleanTileGrid(
                this.projectProps.zoom,
                outputTileRange.minX,
                outputTileRange.minY,
                outputTileRange.getWidth(),
                outputTileRange.getHeight()
            )

            // Generate result name from selected categories and custom queries
            const categoryNames = selectedCategories.map(cat => cat.name)
            const customNames = validCustomQueries.map(q => {
                const valueDisplay = q.value.trim() ? `=${q.value}` : ""
                return `${q.key}${valueDisplay}`
            })
            const allNames = [...categoryNames, ...customNames]
            result.name = allNames.length === 1 ? allNames[0] : `OSM: ${allNames.join(', ')}`

            // Process each selected category sequentially to avoid overwhelming the API
            for (const category of selectedCategories) {
                const code = category.code
                const type = category.type

                if (this.outputCache.has(code)) {
                    // Use cached result - merge into result grid
                    const cachedGrid = this.outputCache.get(code)!
                    result.merge(cachedGrid)
                } else {
                    // Fetch from API with retry logic
                    let json: OverpassResponse
                    try {
                        json = await retrieveLandUseData(this.projectProps.extent, code, type, 3)
                    } catch (error) {
                        console.error(`Failed to retrieve data for ${category.name} after retries:`, error)
                        // Continue with next category instead of failing completely
                        continue
                    }
                    const features = json.elements as Array<OverpassFeature>

                    // Create temporary grid for this category
                    const categoryGrid = new BooleanTileGrid(
                        this.projectProps.zoom,
                        outputTileRange.minX,
                        outputTileRange.minY,
                        outputTileRange.getWidth(),
                        outputTileRange.getHeight()
                    )

                    // Process features into category grid
                    features.forEach((feature) => {
                        if (feature.type === "node") {
                            const [x, y] = (proj4 as any).default('EPSG:4326', 'EPSG:3857', [feature.lon, feature.lat])

                            const p = new Point([x, y])

                            const featureTileRange = tileGrid.getTileRangeForExtentAndZ(
                                p.getExtent(),
                                this.projectProps.zoom
                            )

                            if (
                                featureTileRange.maxX >= outputTileRange.minX &&
                                featureTileRange.maxX <= outputTileRange.maxX &&
                                featureTileRange.minY >= outputTileRange.minY &&
                                featureTileRange.minY <= outputTileRange.maxY
                            ) {
                                categoryGrid.set(featureTileRange.maxX, featureTileRange.minY, mask.get(featureTileRange.maxX, featureTileRange.minY) === true)
                            }

                        } else {
                            const geom = feature.geometry

                            if (!geom || geom.length === 0) return

                            const epsg3857_geometry = geom.map((e) => (proj4 as any).default('EPSG:4326', 'EPSG:3857', [e.lon, e.lat]))

                            // Check if way is closed (first point = last point)
                            const firstPoint = epsg3857_geometry[0]
                            const lastPoint = epsg3857_geometry[epsg3857_geometry.length - 1]
                            const isClosed = firstPoint[0] === lastPoint[0] && firstPoint[1] === lastPoint[1]

                            let featureTileRange: any
                            let geometry: Polygon | LineString

                            if (isClosed) {
                                // Closed way: treat as polygon (area)
                                const closedGeometry = epsg3857_geometry
                                geometry = new Polygon([closedGeometry])
                                featureTileRange = tileGrid.getTileRangeForExtentAndZ(
                                    geometry.getExtent(),
                                    this.projectProps.zoom
                                )

                                for (
                                    let x = Math.max(featureTileRange.minX, outputTileRange.minX);
                                    x <= Math.min(featureTileRange.maxX, outputTileRange.maxX);
                                    ++x
                                ) {
                                    for (
                                        let y = Math.max(featureTileRange.minY, outputTileRange.minY);
                                        y <= Math.min(featureTileRange.maxY, outputTileRange.maxY);
                                        ++y
                                    ) {
                                        const tileExtent = tileGrid.getTileCoordExtent([this.projectProps.zoom, x, y])
                                        if (geometry.intersectsExtent(tileExtent)) {
                                            categoryGrid.set(x, y, mask.get(x, y) === true)
                                        }
                                    }
                                }
                            } else {
                                // Open way: treat as linear feature (line)
                                geometry = new LineString(epsg3857_geometry)
                                featureTileRange = tileGrid.getTileRangeForExtentAndZ(
                                    geometry.getExtent(),
                                    this.projectProps.zoom
                                )

                                // For linear features, only mark tiles that the line actually passes through
                                for (
                                    let x = Math.max(featureTileRange.minX, outputTileRange.minX);
                                    x <= Math.min(featureTileRange.maxX, outputTileRange.maxX);
                                    ++x
                                ) {
                                    for (
                                        let y = Math.max(featureTileRange.minY, outputTileRange.minY);
                                        y <= Math.min(featureTileRange.maxY, outputTileRange.maxY);
                                        ++y
                                    ) {
                                        const tileExtent = tileGrid.getTileCoordExtent([this.projectProps.zoom, x, y])
                                        // Check if line intersects the tile extent
                                        if (geometry.intersectsExtent(tileExtent)) {
                                            categoryGrid.set(x, y, mask.get(x, y) === true)
                                        }
                                    }
                                }
                            }
                        }
                    })

                    // Cache the category grid
                    this.outputCache.set(code, categoryGrid)

                    // Merge into result grid
                    result.merge(categoryGrid)
                }
            }

            // Process custom queries
            for (const customQuery of validCustomQueries) {
                const key = customQuery.key.trim()
                const value = customQuery.value.trim()
                const types = customQuery.type === "all" ? ["node", "way", "relation"] : [customQuery.type]
                
                // Build query code for caching
                const queryCode = value ? `[${key}=${value}]` : `[${key}]`

                // Process each type if "all" is selected
                for (const type of types) {
                    const cacheKey = `${queryCode}_${type}`

                    if (this.outputCache.has(cacheKey)) {
                        // Use cached result - merge into result grid
                        const cachedGrid = this.outputCache.get(cacheKey)!
                        result.merge(cachedGrid)
                    } else {
                        // Build Overpass query filter
                        const queryFilter = value ? `["${key}"="${value}"]` : `["${key}"]`
                        
                        // Fetch from API with retry logic
                        let json: OverpassResponse
                        try {
                            // Ensure 'type' is of the expected OverpassType
                            // If type is not one of "node", "way", or "relation", skip this iteration
                            if (type !== "node" && type !== "way" && type !== "relation") {
                                console.error(`Invalid OSM type: ${type} in custom query for ${key}${value ? '=' + value : ''}`)
                                continue
                            }
                            json = await retrieveLandUseData(this.projectProps.extent, queryFilter, type as "node" | "way" | "relation", 3)
                        } catch (error) {
                            console.error(`Failed to retrieve data for custom query ${key}${value ? '=' + value : ''} (${type}) after retries:`, error)
                            // Continue with next query instead of failing completely
                            continue
                        }
                        const features = json.elements as Array<OverpassFeature>

                    // Create temporary grid for this query
                    const queryGrid = new BooleanTileGrid(
                        this.projectProps.zoom,
                        outputTileRange.minX,
                        outputTileRange.minY,
                        outputTileRange.getWidth(),
                        outputTileRange.getHeight()
                    )

                    // Process features into query grid
                    features.forEach((feature) => {
                        if (feature.type === "node") {
                            const [x, y] = (proj4 as any).default('EPSG:4326', 'EPSG:3857', [feature.lon, feature.lat])

                            const p = new Point([x, y])

                            const featureTileRange = tileGrid.getTileRangeForExtentAndZ(
                                p.getExtent(),
                                this.projectProps.zoom
                            )

                            if (
                                featureTileRange.maxX >= outputTileRange.minX &&
                                featureTileRange.maxX <= outputTileRange.maxX &&
                                featureTileRange.minY >= outputTileRange.minY &&
                                featureTileRange.minY <= outputTileRange.maxY
                            ) {
                                queryGrid.set(featureTileRange.maxX, featureTileRange.minY, mask.get(featureTileRange.maxX, featureTileRange.minY) === true)
                            }

                        } else {
                            const geom = feature.geometry

                            if (!geom || geom.length === 0) return

                            const epsg3857_geometry = geom.map((e) => (proj4 as any).default('EPSG:4326', 'EPSG:3857', [e.lon, e.lat]))

                            // Check if way is closed (first point = last point)
                            const firstPoint = epsg3857_geometry[0]
                            const lastPoint = epsg3857_geometry[epsg3857_geometry.length - 1]
                            const isClosed = firstPoint[0] === lastPoint[0] && firstPoint[1] === lastPoint[1]

                            let featureTileRange: any
                            let geometry: Polygon | LineString

                            if (isClosed) {
                                // Closed way: treat as polygon (area)
                                const closedGeometry = epsg3857_geometry
                                geometry = new Polygon([closedGeometry])
                                featureTileRange = tileGrid.getTileRangeForExtentAndZ(
                                    geometry.getExtent(),
                                    this.projectProps.zoom
                                )

                                for (
                                    let x = Math.max(featureTileRange.minX, outputTileRange.minX);
                                    x <= Math.min(featureTileRange.maxX, outputTileRange.maxX);
                                    ++x
                                ) {
                                    for (
                                        let y = Math.max(featureTileRange.minY, outputTileRange.minY);
                                        y <= Math.min(featureTileRange.maxY, outputTileRange.maxY);
                                        ++y
                                    ) {
                                        const tileExtent = tileGrid.getTileCoordExtent([this.projectProps.zoom, x, y])
                                        if (geometry.intersectsExtent(tileExtent)) {
                                            queryGrid.set(x, y, mask.get(x, y) === true)
                                        }
                                    }
                                }
                            } else {
                                // Open way: treat as linear feature (line)
                                geometry = new LineString(epsg3857_geometry)
                                featureTileRange = tileGrid.getTileRangeForExtentAndZ(
                                    geometry.getExtent(),
                                    this.projectProps.zoom
                                )

                                // For linear features, only mark tiles that the line actually passes through
                                for (
                                    let x = Math.max(featureTileRange.minX, outputTileRange.minX);
                                    x <= Math.min(featureTileRange.maxX, outputTileRange.maxX);
                                    ++x
                                ) {
                                    for (
                                        let y = Math.max(featureTileRange.minY, outputTileRange.minY);
                                        y <= Math.min(featureTileRange.maxY, outputTileRange.maxY);
                                        ++y
                                    ) {
                                        const tileExtent = tileGrid.getTileCoordExtent([this.projectProps.zoom, x, y])
                                        // Check if line intersects the tile extent
                                        if (geometry.intersectsExtent(tileExtent)) {
                                            queryGrid.set(x, y, mask.get(x, y) === true)
                                        }
                                    }
                                }
                            }
                        }
                    })

                        // Cache the query grid
                        this.outputCache.set(cacheKey, queryGrid)

                        // Merge into result grid
                        result.merge(queryGrid)
                    }
                }
            }

            // Set output
            editorNode.meta.output = outputs['out'] = result

            const previewControl: any = editorNode.controls.get('Preview')
            previewControl?.update()
            editorNode.update()

        } catch (error) {
            console.error('Error in OSMLandUseComponentV2 worker:', error)
            // Set error state
            const tileGrid = createXYZ()
            const outputTileRange = tileGrid.getTileRangeForExtentAndZ(
                this.projectProps.extent,
                this.projectProps.zoom
            )
            const errorResult = new BooleanTileGrid(
                this.projectProps.zoom,
                outputTileRange.minX,
                outputTileRange.minY,
                outputTileRange.getWidth(),
                outputTileRange.getHeight()
            )
            errorResult.name = "Error loading OSM data"
            editorNode.meta.output = outputs['out'] = errorResult
            
            const previewControl: any = editorNode.controls.get('Preview')
            previewControl?.update()
            editorNode.update()
        }
    }
}
